import { ApiError } from "../lib/api";
import { IconAlertTriangle, IconInfo } from "./icons";

/**
 * Aviso de falha ao carregar dados da API.
 *
 * Existe para que um erro não se passe por dado: sem ele, a tela mostrava "0",
 * "—" ou "sem dados", e quem lê conclui que o valor é zero. O aviso fica no topo
 * e o resto da tela continua como está (com os valores ausentes, ou com os da
 * última carga que deu certo, se houver).
 */
export function QueryErrorBanner({
  onRetry,
  what = "os dados desta tela",
  retrying = false,
  error,
}: {
  onRetry?: () => void;
  /** o que deixou de carregar, para a frase ("os dados desta tela", "a lista de O.S.") */
  what?: string;
  retrying?: boolean;
  /** com 403, o aviso diz que é indisponível (não falha), sem "Tentar de novo" */
  error?: unknown;
}) {
  // 403 não é falha passageira: o perfil ou o ambiente (ex.: demonstração) não
  // dá acesso. Tentar de novo devolveria o mesmo 403.
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div role="status" className="flex items-start gap-3 rounded-lg border border-surface-border bg-surface-muted px-4 py-3">
        <IconInfo className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">Não é possível ver {what} aqui.</p>
          <p className="text-xs text-gray-600 mt-0.5">{error.message || "Seu acesso não inclui esta informação."}</p>
        </div>
      </div>
    );
  }
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-danger-100 bg-danger-50 px-4 py-3">
      <IconAlertTriangle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-danger-700">Não foi possível carregar {what}.</p>
        <p className="text-xs text-danger-700/80 mt-0.5">
          Os valores abaixo podem estar ausentes ou desatualizados. Um campo vazio aqui não significa zero.
        </p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-danger-100 bg-white text-danger-700 hover:bg-danger-100 disabled:opacity-60"
        >
          {retrying ? "Tentando…" : "Tentar de novo"}
        </button>
      )}
    </div>
  );
}
